import React, { ReactElement, useEffect } from 'react';
import styled from 'styled-components';
import { SystemIconProps } from '../../../Icon/SystemIcon';
import { Tooltip } from '../../../Tooltip';
import { Skeleton } from '../../../Skeleton/Skeleton';
import { Typography } from '../../../Typography/Typography';
import { useDataTableContext } from '../../utils/DataTableContext';
import { StyledToolbarMessageSkeleton, StyledToolbarMessage } from '../Message';
import { useIntlUds } from '../../../../translations/UdsIntlProvider';
import { isNumber } from '../../../../utils/guards';
import { BasePlugin } from '../../plugins';
import { BulkActionButton } from './BulkActionButton';
import { useBulkActionsOverflowMenu } from './useBulkActionsOverflowMenu';

const isTestEnv = process.env.NODE_ENV === 'test';

export interface DataTableBulkAction<D extends object> {
  callback: (selectedRows: D[]) => void;
  disabled: boolean | ((selectedRows: D[]) => boolean);
  tooltipText: string;
}

export interface DataTableBulkActionsProps<D extends object> {
  children: Array<ReactElement | null> | ReactElement | null;
  dataTest?: string;
  selectionText?: string;
  onSelect?: (selectedRows: D[]) => void;
}

interface CustomBulkActionProps<D extends object> {
  iconName?: SystemIconProps['name'];
  disabledMessage?: string;
  text?: string;
  disabled?: boolean | ((selectedRows: D[]) => boolean);
  tooltipText?: string;
  onClick: (selectedRows: D[]) => void;
  dataTest?: string;
  color?: 'inherit' | 'primary' | 'secondary' | 'default';
}

interface PreDefinedActionProps<D extends object> {
  disabled?: boolean | ((selectedRows: D[]) => boolean);
  disabledMessage?: string;
  tooltipText?: string;
  text?: string;
  onClick: (selectedRows: D[]) => void;
}

export const CustomBulkAction = <D extends object>({
  iconName,
  disabled,
  onClick,
  tooltipText,
  disabledMessage,
  dataTest = 'custom-bulk-action',
  text,
  color
}: CustomBulkActionProps<D>) => {
  const { selectedRows } = useDataTableContext<D>();

  const hideActions = !selectedRows?.length;
  if (hideActions || !text) return null;

  const isDisabled = typeof disabled === 'function' ? disabled(selectedRows) : disabled;
  const tooltipTitle = isDisabled ? disabledMessage : tooltipText;
  return (
    <Tooltip title={tooltipTitle ?? ''} disabled={!tooltipTitle} compact={false} className="button-container">
      <BulkActionButton
        color={color}
        startIcon={iconName}
        disabled={isDisabled}
        dataTest={dataTest}
        onClick={() => onClick(selectedRows)}
      >
        <span data-test="bulk-action-label">{text}</span>
      </BulkActionButton>
    </Tooltip>
  );
};

export const EditBulkAction = <D extends object>({
  disabled,
  onClick,
  tooltipText,
  text,
  disabledMessage
}: PreDefinedActionProps<D>) => {
  const { intl } = useIntlUds();

  return (
    <CustomBulkAction
      disabledMessage={disabledMessage}
      iconName="edit"
      text={text ?? intl.DataTable.BulkActions.edit}
      disabled={disabled}
      onClick={onClick}
      tooltipText={tooltipText || intl.DataTable.BulkActions.edit}
      dataTest="data-table-bulk-edit"
    />
  );
};

export const DeleteBulkAction = <D extends object>({
  disabled,
  onClick,
  tooltipText,
  text,
  separator = true,
  disabledMessage
}: PreDefinedActionProps<D> & { separator?: boolean }) => {
  const { intl } = useIntlUds();

  return (
    <StyledDeleteAction>
      {separator && <BulkActionSeparator className="bulk-action-separator" />}
      <CustomBulkAction
        iconName="delete"
        disabledMessage={disabledMessage}
        text={text ?? intl.DataTable.BulkActions.delete}
        disabled={disabled}
        onClick={onClick}
        tooltipText={tooltipText || intl.DataTable.BulkActions.delete}
        dataTest="data-table-bulk-delete"
      />
    </StyledDeleteAction>
  );
};

const StyledDeleteAction = styled.div`
  display: flex;
`;

// eslint-disable-next-line styled-components-varname/varname
export const BulkActionSeparator = styled.div`
  height: 36px;
  width: 1px;
  background-color: ${({ theme }) => theme.colorSurfaceVariant};
  margin: 0 ${({ theme }) => theme.spacingSize.m};
`;

export const DataTableBulkActions = <D extends object>({
  children,
  dataTest = 'data-table-bulk-actions',
  selectionText,
  onSelect
}: DataTableBulkActionsProps<D>): ReactElement | null => {
  const { totalCount, isFetching, getGroupedSelectedRowModel, getState, setSelectedRows, selectedRows } =
    useDataTableContext<D>();
  const hideActions = !selectedRows?.length || (selectedRows && selectedRows.length < 1);
  const { intl, format } = useIntlUds();
  const newSelectedRows = getGroupedSelectedRowModel?.();

  useEffect(() => {
    const selectedOriginal = newSelectedRows?.rows.map((row) => row.original) ?? [];
    const pageIndex = getState?.().pagination.pageIndex?.toString() ?? '';
    setSelectedRows(selectedOriginal, pageIndex);
  }, [getState, newSelectedRows?.rows, setSelectedRows]);

  const { overflowMenuWithActions, bulkActions, isResizing } = useBulkActionsOverflowMenu({ children, hideActions });

  // biome-ignore lint/correctness/useExhaustiveDependencies: exception
  useEffect(() => {
    onSelect?.(selectedRows || []);
  }, [selectedRows?.length]);

  return (
    <BasePlugin
      id="bulk-actions"
      key="bulk-actions"
      bulkActions={{
        Component: !hideActions ? (
          <StyledBulkActions key="bulk-actions" data-test={dataTest}>
            <StyledToolbarMessage data-test="count-selected-rows-message" $isFetching={isFetching}>
              <StyledMessage noWrap dataTest="bulk-action-message" $isFetching={isFetching}>
                {selectedRows?.length ?? 0}{' '}
                {selectionText ||
                  (totalCount === 1
                    ? intl.DataTable.BulkActions.selected.singular
                    : format(intl.DataTable.BulkActions.selected.plural, {
                        totalCount: isNumber(totalCount)
                          ? totalCount.toString()
                          : intl.DataTable.BulkActions.selected.unknown
                      }))}
              </StyledMessage>
              {isFetching && (
                <StyledToolbarMessageSkeleton>
                  <StyledSkeleton width="100%" />
                </StyledToolbarMessageSkeleton>
              )}
            </StyledToolbarMessage>
            <StyledActions $isResizing={isTestEnv ? false : isResizing}>
              {bulkActions}
              {overflowMenuWithActions}
            </StyledActions>
          </StyledBulkActions>
        ) : (
          <React.Fragment key="hidden-bulk-actions" />
        )
      }}
    />
  );
};

const StyledMessage = styled(Typography)<{ $isFetching?: boolean }>`
  && {
    // Required to hide message if items were selected before the fetch started.
    display: ${({ $isFetching }) => ($isFetching ? 'none' : 'block')};
    top: ${({ $isFetching }) => ($isFetching ? '-2px' : '0')};
  }
`;

const StyledActions = styled.div<{ $isResizing: boolean }>`
  opacity: ${({ $isResizing }) => ($isResizing ? 0 : 1)};
  transition: ${({ $isResizing }) => ($isResizing ? 'opacity 0' : 'opacity 0.3s')};
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${({ theme }) => theme.spacingSize.m};
`;

const StyledBulkActions = styled.div`
  background-color: ${({ theme }) => theme.colorSurface};
  padding-left: ${({ theme }) => theme.spacingSize.xl};
  position: absolute;
  height: 100%;
  width: 100%;
  left: 0;
  top: 0;
  z-index: 100;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${({ theme }) => theme.spacingSize.m};

  &::before {
    content: '';
    pointer-events: none;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.surfaceTonesLevel1};
  }

  button.MuiButton-root.outlined.default {
    background-color: transparent;
    color: ${({ theme }) => theme.colorOnSurface};
    font-weight: 400;

    &:hover {
      background-color: ${({ theme }) => theme.colorSurface};
      color: ${({ theme }) => theme.colorOnSurface};
    }
  }

  .button-container + .button-container,
  .button-container + button,
  button + .button-container,
  button + button {
    margin-left: ${({ theme }) => theme.spacingSize.m};
  }
`;

const StyledSkeleton = styled(Skeleton)`
  height: 100%;
  position: relative;
  top: -2px;
`;
